import {
  MetaModel,
  type APIFieldAttributes,
  type Workspace,
} from '@ardoq/api-types';
import {
  isArdoqError,
  isTruthy,
  partialPayload,
  pipeReducers,
} from '@ardoq/common-helpers';
import { MetamodelViewGraphEdge, MetaModelViewGraphNode } from '@ardoq/graph';
import {
  ARDOQ_DEFAULT_LOCALE,
  type Locale,
  localeCompare,
} from '@ardoq/locale';
import { MetaModelLoadedState } from 'architectureModel/types';
import { metaModelOperations } from 'architectureModel/metaModelOperations';
import { StreamCollection } from 'streams/utils/streamUtils';
import { getId } from 'viewpointBuilder/getId';
import { fieldOps } from '../../models/utils/fieldOps';
import {
  type ToggleExpandableSectionPayload,
  type ToggleIsSelectedGraphNodePayload,
} from './actions';
import { MetamodelViewState } from './types';

const getInitialState = (): MetamodelViewState => ({
  rawGraphEdges: new Map(),
  rawGraphNodes: new Map(),
  graphNodes: new Map(),
  graphEdges: new Map(),
  graphGroups: new Map(),
  selectedGraphNodeId: null,
  selectedComponentType: null,
  typeNameToWorkspaceMap: new Map(),
  componentTypeNameToFieldMap: new Map(),
  referenceTypeNameToFieldMap: new Map(),
  fieldMap: new Map(),
  expandedSection: {
    workspaces: true,
    fields: true,
  },
  workspaceMap: new Map(),
  selectedFields: null,
  selectedWorkspaces: null,
  shouldFitToContentOnLayout: true,
  isMetaModelLoaded: false,
  locale: ARDOQ_DEFAULT_LOCALE,
});

const createMetamodel = (
  state: MetamodelViewState,
  metaModel: MetaModel
): MetamodelViewState => {
  const rawGraphNodes = metaModelOperations.createRawGraphNodes(metaModel);
  const rawGraphEdges = metaModelOperations.createRawGraphEdges(metaModel);

  const graphNodes = new Map<string, MetaModelViewGraphNode>(
    Array.from(rawGraphNodes).map(([id, { modelId, metaData }]) => {
      // In the metamodel view each type should just appear once, so we can use
      // the rawGraphNodes id as the graphNode id.
      const graphNode = MetaModelViewGraphNode.create(modelId, id, metaData);
      return [id, graphNode];
    })
  );

  const graphEdges = new Map<string, MetamodelViewGraphEdge>(
    Array.from(rawGraphEdges).map(
      ([_, { modelId, sourceId, targetId, metaData }]) => {
        const graphEdge = MetamodelViewGraphEdge.create(
          modelId,
          sourceId,
          targetId,
          graphNodes,
          getId(),
          metaData
        );
        return [graphEdge.id, graphEdge];
      }
    )
  );

  return {
    ...state,
    rawGraphNodes,
    rawGraphEdges,
    graphNodes,
    graphEdges,
  };
};

const createFieldMap = <State>(
  state: State,
  { byId }: StreamCollection<APIFieldAttributes>
): State => {
  const fieldMap = new Map(
    Object.values(byId).map(field => {
      return [field.name, field];
    })
  );

  return {
    ...state,
    fieldMap,
  };
};

const createWorkspaceMap = (
  state: MetamodelViewState,
  { byId }: StreamCollection<Workspace>
): MetamodelViewState => {
  const workspaceMap = new Map(
    Object.values(byId).map(({ _id, name }) => [_id, name])
  );

  return {
    ...state,
    workspaceMap,
  };
};

const typeNameToWorkspaceAndFieldMaps = (
  state: MetamodelViewState,
  metaModelResult: MetaModelLoadedState
): MetamodelViewState => {
  if (isArdoqError(metaModelResult)) {
    return state;
  }

  return pipeReducers(
    {
      ...state,
      ...metaModelOperations.typeNameToWorkspaceAndFieldMaps(metaModelResult),
      isMetaModelLoaded: true,
    },
    partialPayload(createMetamodel, metaModelResult)
  );
};

const toggleIsSelectedGraphNode = (
  state: MetamodelViewState,
  { graphNodeId }: ToggleIsSelectedGraphNodePayload
) => {
  const newGraphNodes = new Map(state.graphNodes);
  Array.from(newGraphNodes.values()).forEach(graphNode => {
    graphNode.setIsSelected(false);
  });

  const isNewNodeSelected = Boolean(
    graphNodeId && graphNodeId !== state.selectedGraphNodeId
  );
  if (isNewNodeSelected) {
    newGraphNodes.get(graphNodeId)!.setIsSelected(true);
  }

  const selectedComponentType = isNewNodeSelected
    ? state.rawGraphNodes.get(graphNodeId)!.metaData
    : null;

  return {
    ...state,
    graphNodes: newGraphNodes,
    selectedGraphNodeId: isNewNodeSelected ? graphNodeId : null,
    selectedComponentType,
    selectedFields: isNewNodeSelected
      ? getSelectedFields(state, graphNodeId, state.locale)
      : null,
    selectedWorkspaces: isNewNodeSelected
      ? getSelectedWorkspaces(state, graphNodeId, state.locale)
      : null,
    shouldFitToContentOnLayout: false,
  };
};

const START_DATE_FIELD_NAME_SUFFIX = '_start_date';
const END_DATE_FIELD_NAME_SUFFIX = '_end_date';

const getSelectedFields = (
  { componentTypeNameToFieldMap, fieldMap, graphNodes }: MetamodelViewState,
  graphNodeId: string,
  locale: Locale
) => {
  const name = graphNodes.get(graphNodeId)?.getLabel() ?? '';
  const fields = componentTypeNameToFieldMap.get(name) ?? [];

  const startDateCandidates = new Set(
    fields
      .filter(fieldName => fieldName.endsWith(END_DATE_FIELD_NAME_SUFFIX))
      .map(fieldName =>
        fieldName.replace(
          END_DATE_FIELD_NAME_SUFFIX,
          START_DATE_FIELD_NAME_SUFFIX
        )
      )
  );

  const dateRangeFields = fields.filter(fieldName =>
    startDateCandidates.has(fieldName)
  );
  const endDates = new Set(
    dateRangeFields.map(fieldName =>
      fieldName.replace(
        START_DATE_FIELD_NAME_SUFFIX,
        END_DATE_FIELD_NAME_SUFFIX
      )
    )
  );
  const dateRangeFieldsSet = new Set(dateRangeFields);
  return fields
    .filter(fieldName => !endDates.has(fieldName))
    .map(fieldName => {
      const isDateRangeField = dateRangeFieldsSet.has(fieldName);
      const field = fieldMap.get(fieldName);
      if (!field) return null;
      const { label } = field;
      const isCalculatedField = fieldOps.isCalculatedField(field);
      const type = isDateRangeField ? 'Date Range' : field.type;
      return {
        label: isDateRangeField ? label.replace('.Start', '') : label,
        type: isCalculatedField ? `${type} (Calculated Field)` : type,
      };
    })
    .filter(isTruthy)
    .sort((a, b) => localeCompare(a.label, b.label, locale));
};

const getSelectedWorkspaces = (
  { typeNameToWorkspaceMap, workspaceMap, graphNodes }: MetamodelViewState,
  graphNodeId: string,
  locale: Locale
) => {
  const name = graphNodes.get(graphNodeId)?.getLabel() ?? '';
  const workspaces = typeNameToWorkspaceMap.get(name) ?? [];

  return workspaces
    .map(workspaceId => workspaceMap.get(workspaceId) ?? null)
    .filter(isTruthy)
    .sort((a, b) => localeCompare(a, b, locale));
};

const toggleExpandableSection = (
  state: MetamodelViewState,
  { expandableId }: ToggleExpandableSectionPayload
) => {
  return {
    ...state,
    expandedSection: {
      ...state.expandedSection,
      [expandableId]: !state.expandedSection[expandableId],
    },
  };
};

export const metamodelViewOperations = {
  getInitialState,
  createFieldMap,
  typeNameToWorkspaceAndFieldMaps,
  createWorkspaceMap,
  toggleIsSelectedGraphNode,
  toggleExpandableSection,
};
